import type { ReactNode } from "react";
import { useTranslations } from "use-intl";
import { Divider } from "@mui/material";
import clsx from "clsx";
import { MAX_TRAVELLERS, MIN_CHILD_AGE } from "@/app/lib/constants";
import { CollapsibleCard } from "../../CollapsibleCard";
import type { SelectedTravellers } from "../../types";
import { CounterButton } from "./CounterButton";

type TravellerSelectionInputProps = {
  title: ReactNode;
  subtitle: ReactNode;
  step?: number;
  value: number;
  maxValue?: number;
  minValue?: number;
  plusButtonDisabled?: boolean;
  onChange: (newValue: number) => void;
};

function TravellerSelectionInput({
  title,
  subtitle,
  step,
  value,
  maxValue,
  minValue,
  plusButtonDisabled,
  onChange,
}: TravellerSelectionInputProps) {
  return (
    <div className="flex items-center justify-between gap-2 py-3">
      <div className="text-sm">
        <div className="font-medium text-text-primary">{title}</div>
        <div className="text-text-disabled-primary">{subtitle}</div>
      </div>

      <div className="shrink-0">
        <CounterButton
          step={step}
          value={value}
          maxValue={maxValue}
          minValue={minValue}
          plusButtonDisabled={plusButtonDisabled}
          onChange={onChange}
        />
      </div>
    </div>
  );
}

type Props = {
  spotAvailability: number | undefined;
  selectedTravellers: SelectedTravellers;
  onChange: (newSelectedTravellers: SelectedTravellers) => void;
};

export function TravellersSelectionCard({
  spotAvailability,
  selectedTravellers,
  onChange,
}: Props) {
  const t = useTranslations();

  const selectedCount = Object.entries(selectedTravellers).reduce(
    (sum, [travellerType, count]) => {
      // infants do not take up spots
      if (travellerType === "infant") {
        return sum;
      }

      return sum + count;
    },
    0,
  );

  const spotsLeft = spotAvailability
    ? Math.max(spotAvailability - selectedCount, 0)
    : undefined;

  const divider = (
    <Divider
      sx={{
        borderColor: "var(--color-stroke-neutral-soft)",
        my: "calc(var(--spacing))",
      }}
    />
  );

  return (
    <CollapsibleCard
      title={t("trips.travellersSelection.title")}
      collapsible={false}
    >
      {spotsLeft !== undefined ? (
        <div
          className={clsx(
            "mb-3 rounded-xl p-2 text-sm font-medium transition-colors",
            "before:me-2 before:inline-block before:size-3 before:rounded-full before:border-[3px] before:align-middle",
            {
              "bg-surface-accent-softer text-surface-accent-strong before:border-surface-accent-soft before:bg-core-accent-500":
                spotsLeft >= 4,
              "bg-amber-100 text-amber-800 before:border-amber-200 before:bg-amber-500":
                spotsLeft < 4 && spotsLeft > 0,
              "bg-red-100 text-red-800 before:border-red-200 before:bg-red-500":
                spotsLeft <= 0,
            },
          )}
        >
          {t("trips.travellersSelection.spotsLeft", { spotsLeft })}
        </div>
      ) : null}

      <TravellerSelectionInput
        title={t("trips.travellersSelection.adultInSingleRoom.title")}
        subtitle={t("trips.travellersSelection.adultInSingleRoom.subtitle")}
        value={selectedTravellers.adultInSingleRoom}
        onChange={(value) => {
          onChange({
            ...selectedTravellers,
            adultInSingleRoom: value,
          });
        }}
        plusButtonDisabled={!spotsLeft}
      />

      {divider}

      <TravellerSelectionInput
        title={t("trips.travellersSelection.adultInDoubleRoom.title")}
        subtitle={t("trips.travellersSelection.adultInDoubleRoom.subtitle")}
        // only even numbers for double rooms should be allowed
        step={2}
        value={selectedTravellers.adultInDoubleRoom}
        onChange={(value) => {
          onChange({
            ...selectedTravellers,
            adultInDoubleRoom: value,
          });
        }}
        plusButtonDisabled={(spotsLeft || 0) < 2}
      />

      {divider}

      <TravellerSelectionInput
        title={t("trips.travellersSelection.childWithBed.title")}
        subtitle={t("trips.travellersSelection.childWithBed.subtitle")}
        value={selectedTravellers.childWithBed}
        onChange={(value) => {
          onChange({
            ...selectedTravellers,
            childWithBed: value,
          });
        }}
        plusButtonDisabled={!spotsLeft}
      />

      {divider}

      <TravellerSelectionInput
        title={t("trips.travellersSelection.childWithoutBed.title")}
        subtitle={t("trips.travellersSelection.childWithoutBed.subtitle")}
        value={selectedTravellers.childWithoutBed}
        onChange={(value) => {
          onChange({
            ...selectedTravellers,
            childWithoutBed: value,
          });
        }}
        plusButtonDisabled={!spotsLeft}
      />

      {divider}

      <TravellerSelectionInput
        title={t("trips.travellersSelection.infant.title")}
        subtitle={t("trips.travellersSelection.infant.subtitle", {
          infantAge: MIN_CHILD_AGE,
        })}
        maxValue={MAX_TRAVELLERS}
        value={selectedTravellers.infant}
        onChange={(value) => {
          onChange({
            ...selectedTravellers,
            infant: value,
          });
        }}
      />
    </CollapsibleCard>
  );
}
